import { useMemo } from 'react';

import type { MeResponse } from '../widget-api/auth-me';
import { fetchPublicBranches } from '../widget-api/public-branches';
import { useAsync } from './useAsync';

export type TrainerBranchOption = {
  id: number;
  name: string;
};

/** Роли, при которых человек в студии — не «только тренер» (как IsTrainerOnlyInOrg на сервере). */
const ELEVATED_ORG_ROLES = new Set([
  'staff',
  'org_main_admin',
  'admin',
  'branch_admin',
  'manager',
  'superadmin',
  'platform_staff'
]);

const PLATFORM_ROLES = new Set(['superadmin', 'platform_staff']);

function normalizeRole(role: string | null | undefined): string {
  return (role ?? '').trim().toLowerCase();
}

function hasElevatedMembership(me: MeResponse): boolean {
  const orgId = me.organization_id;
  for (const membership of me.memberships ?? []) {
    if (orgId != null && membership.organization_id !== orgId) continue;
    if (ELEVATED_ORG_ROLES.has(normalizeRole(membership.role))) return true;
  }
  return false;
}

/**
 * «Только тренер» в текущей студии (зеркало серверного IsTrainerOnlyInOrg).
 * Роль берём из контекста входа (me.context.role), для старых сессий — из me.role.
 * Платформенные роли (суперадмин/персонал платформы) «только тренером» не считаются.
 */
export function isTrainerOnlyInCurrentOrg(me: MeResponse | null | undefined): boolean {
  if (!me) return false;
  const topRole = normalizeRole(me.role);
  if (PLATFORM_ROLES.has(topRole)) return false;

  const contextRole = normalizeRole(me.context?.role);
  const effectiveRole = contextRole || topRole;
  if (PLATFORM_ROLES.has(effectiveRole)) return false;
  if (effectiveRole !== 'trainer') return false;
  return !hasElevatedMembership(me);
}

/** Филиалы из trainer_placements (для «только тренера»). */
export function trainerPlacementBranchOptions(me: MeResponse | null | undefined): TrainerBranchOption[] {
  const seen = new Set<number>();
  const out: TrainerBranchOption[] = [];
  for (const p of me?.trainer_placements ?? []) {
    if (p.branch_id == null || p.branch_id <= 0 || seen.has(p.branch_id)) continue;
    seen.add(p.branch_id);
    out.push({ id: p.branch_id, name: p.branch_name?.trim() || `Филиал #${p.branch_id}` });
  }
  return out;
}

/**
 * Источник филиалов для экранов приложения тренера:
 * - «только тренер» — только его trainer_placements;
 * - админ/главный админ/staff/суперадмин/платформа — все филиалы выбранной организации.
 *
 * Публичный список филиалов отдаёт филиалы всех организаций, поэтому фильтруем
 * строго по `me.organization_id` (выбранной студии суперадмина).
 */
export function useTrainerBranchOptions(me: MeResponse | null): {
  branchOptions: TrainerBranchOption[];
  allOrgBranches: boolean;
  loading: boolean;
  error: string | null;
} {
  const allOrgBranches = me != null && !isTrainerOnlyInCurrentOrg(me);
  const orgId = me?.organization_id ?? null;

  const placementOptions = useMemo(() => trainerPlacementBranchOptions(me), [me]);

  const { data, loading, error } = useAsync(async () => {
    if (!allOrgBranches || orgId == null) return [];
    const list = await fetchPublicBranches();
    if (!Array.isArray(list)) return [];
    const seen = new Set<number>();
    const out: TrainerBranchOption[] = [];
    for (const branch of list) {
      if (branch.organization_id !== orgId || branch.id <= 0 || seen.has(branch.id)) continue;
      seen.add(branch.id);
      out.push({ id: branch.id, name: branch.name.trim() || `Филиал #${branch.id}` });
    }
    return out;
  }, [allOrgBranches, orgId]);

  return {
    branchOptions: allOrgBranches ? data ?? [] : placementOptions,
    allOrgBranches,
    loading: allOrgBranches && loading,
    error: allOrgBranches ? error : null
  };
}
